/** Size of the logo shown in the branded viewer header. */
export type BrandLogoSize = 'sm' | 'md' | 'lg' | 'xl';
/** Light or dark chrome for the branded share/viewer page. */
export type BrandTheme = 'dark' | 'light';

export interface MediaBrandSettings {
  id: string;
  user_id: string;
  agency_name: string | null;
  primary_color: string;
  secondary_color: string;
  logo_url: string | null;
  logo_position: 'top-left' | 'top-right';
  /** Header logo size on the viewer/share page. Defaults to 'md'. */
  logo_size?: BrandLogoSize;
  /** Optional custom page background colour (hex). Null = theme default. */
  background_color?: string | null;
  /** Overall viewer chrome theme. Defaults to 'dark'. */
  theme?: BrandTheme;
  cta_label: string | null;
  cta_url: string | null;
  show_social_share: boolean;
  logo_intro_enabled: boolean;
  logo_outro_enabled: boolean;
  logo_bumper_duration: number;
  logo_bumper_tagline: string | null;
  created_at: string;
  updated_at: string;
}

export interface MediaBrandProfile {
  id: string;
  user_id: string;
  name: string;
  agency_name: string | null;
  primary_color: string;
  secondary_color: string;
  logo_url: string | null;
  logo_position: 'top-left' | 'top-right';
  /** Header logo size on the viewer/share page. Defaults to 'md'. */
  logo_size?: BrandLogoSize;
  /** Optional custom page background colour (hex). Null = theme default. */
  background_color?: string | null;
  /** Overall viewer chrome theme. Defaults to 'dark'. */
  theme?: BrandTheme;
  cta_label: string | null;
  cta_url: string | null;
  show_social_share: boolean;
  logo_intro_enabled: boolean;
  logo_outro_enabled: boolean;
  logo_bumper_duration: number;
  logo_bumper_tagline: string | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export type MediaBrandProfileInput = Omit<MediaBrandProfile, 'id' | 'user_id' | 'created_at' | 'updated_at'>;

export interface BrandedViewerConfig {
  appName: string;
  appUrl: string;
  appTagline: string;
  /** Optional full logo shown in the share-page footer (“Powered by”). */
  appLogoUrl?: string;
  rehubUrl: string;
  gradientFrom: string;
  gradientTo: string;
  accentHover: string;
  /** 'portrait' for 9:16 (VideoMotion), 'landscape' for responsive (ImageMotion) */
  aspectMode: 'portrait' | 'landscape';
  showEmbedCode: boolean;
}

export interface SharedVideoData {
  project_id: string;
  project_name: string | null;
  video_url: string;
  /** Per-video orientation override; falls back to the app config's aspectMode. */
  orientation?: 'portrait' | 'landscape' | null;
  logo_url: string | null;
  created_at: string;
  is_promoted?: boolean;
  brand_agency_name?: string | null;
  brand_primary?: string | null;
  brand_secondary?: string | null;
  brand_logo_position?: string | null;
  brand_cta_label?: string | null;
  brand_cta_url?: string | null;
  brand_show_social?: boolean | null;
  audio_tracks?: AudioTrack[];
}

export interface AudioTrack {
  id: string;
  track_type: 'music' | 'voiceover';
  label: string | null;
  audio_url: string;
  volume: number;
  start_offset_seconds: number;
  fade_in: boolean;
  fade_out: boolean;
  is_active: boolean;
}

export interface ActiveSpaceInfo {
  currentCount: number;
  limit: number;
  available: boolean;
}

export type AnalyticsHandler = (
  eventType: 'view' | 'play' | 'complete' | 'share_click',
  extra?: {
    watchDurationMs?: number;
    watchPercentage?: number;
    sharePlatform?: string;
  }
) => void;

export const VIDEOMOTION_CONFIG: BrandedViewerConfig = {
  appName: 'VideoMotion',
  appUrl: 'https://videomotion.rehub.studio',
  appTagline: 'Turn property walkthroughs into polished social-ready edits in minutes',
  rehubUrl: 'https://rehub.studio',
  gradientFrom: 'from-purple-600',
  gradientTo: 'to-pink-600',
  accentHover: 'hover:text-purple-400',
  aspectMode: 'portrait',
  showEmbedCode: true,
};

export const IMAGEMOTION_CONFIG: BrandedViewerConfig = {
  appName: 'ImageMotion',
  appUrl: 'https://imagemotion.rehub.studio',
  appTagline: 'Turn your photos into cinematic videos in minutes',
  appLogoUrl: 'https://imagemotion.rehub.studio/Logo.webp',
  rehubUrl: 'https://rehub.studio',
  gradientFrom: 'from-blue-600',
  gradientTo: 'to-purple-600',
  accentHover: 'hover:text-blue-400',
  aspectMode: 'landscape',
  showEmbedCode: false,
};
